/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { Fragment } from 'react';
import { DashboardColumn, DashboardEmptyColumn, DashboardRow } from '../../../../components/dashboard';
import { getChartComponent, LayoutColumn, LayoutColumnType, LayoutRow } from '../utils/layout';
import { GenericChart } from './GenericChart';
import { GenericData } from './GenericData';

interface LayoutProps {
  getHighlightButton: (column: LayoutColumn) => JSX.Element | undefined
  layout: LayoutRow[];
}

const LayoutTitle = ({ title }: { title?: string }) => {
  if (!title) {
    return null;
  }
  return <DashboardRow
    title={title}
    headingStyle={2}
    className='mt-4 text-ThemeHeadingLight'
    mb={2}
  />
}

const ChartOrData = ({ col, columnCount }: { col: LayoutRow['columns'][0], columnCount: number }) => {
  switch (col.type) {
    case LayoutColumnType.GenericChart:
      return <GenericChart {...col.chartProps} width={getChartWidth(columnCount)} height={getChartHeight(columnCount)} />
    case LayoutColumnType.GenericData:
      return <GenericData {...col.dataProps} width={getChartWidth(columnCount)} />
    case LayoutColumnType.CustomComponent:
      const component = { component: getChartComponent(col.component) }
      return <component.component />
    default:
      return null;
  }
}

const widths = [920, 440, 274];
const heights = [300, 300, 120];

const getFlexBasisPc = (count: number) => `${Math.round(100 / count)}%`;
const getChartHeight = (count: number) => heights[count - 1] ?? heights[0];
const getChartWidth = (count: number) => widths[count - 1] ?? widths[0];
const MAX_COLS = 3;

const createRowLayout = (row: LayoutRow, maxCols: number) => {
  const rows: LayoutColumn[][] = [];
  let currentRow: LayoutColumn[] = [];

  let columnsRendered = 0;
  row.columns.forEach(col => {

    if (col.columnSize === maxCols) {
      if (currentRow.length > 0) {
        rows.push(currentRow)
      }
      rows.push([col])
      currentRow = [];
      columnsRendered = 0;
      return
    }

    if (columnsRendered === maxCols) {
      rows.push(currentRow)
      currentRow = [];
      columnsRendered = 0;
      return
    }

    currentRow.push(col)
    columnsRendered += 1;
  })
  rows.push(currentRow);

  return rows;
};


export const Layout = (props: LayoutProps) => {
  const { layout, getHighlightButton } = props;

  const createColumn = (col: LayoutColumn, columnCount: number) => {
    const flexBasisPc = getFlexBasisPc(columnCount);
    return (
      <DashboardColumn key={`${col.refCode}`} title={col.title} flexBasisPc={flexBasisPc}>
        <div className='pt-2 h-100 d-flex align-bottom position-relative'>
          <ChartOrData col={col} columnCount={columnCount} />
          {getHighlightButton(col)}
        </div>
      </DashboardColumn>
    );
  };

  const getColumnCount = (col: LayoutColumn, fallbackCount: number) => {
    if (col.columnSize) {
      return Math.floor(MAX_COLS / col.columnSize);
    }
    return fallbackCount;
  }

  const getDashboardRows = (rowLayout: LayoutColumn[][], layoutRow: LayoutRow, layoutRowIndex: number) => {
    return rowLayout.map((columns) => {
      const rowColumnCount = layoutRow.columnCount ?? columns.length;
      const dashboardRow: JSX.Element[] = [];
      let colCount = 0;
      for (const col of columns) {
        colCount += col.columnSize ?? 1;
        const columnCount = getColumnCount(col, rowColumnCount ?? columns.length);
        dashboardRow.push(createColumn(col, columnCount));

        // Skip early for max size
        if (col.columnSize === rowColumnCount) {
          return dashboardRow;
        }
      }

      if (colCount < rowColumnCount) {
        const flexBasisPc = getFlexBasisPc(rowColumnCount);
        for (let filler = colCount; filler < rowColumnCount; filler++) {
          dashboardRow.push(
            <DashboardEmptyColumn
              key={`${layoutRow.title}-${layoutRowIndex}-filler-${filler}`}
              flexBasisPc={flexBasisPc}
            />
          );
        }
      }
      return dashboardRow;
    });
  };

  return (
    <>
      {layout.map((layoutRow, i) => {
        const rowLayout = createRowLayout(layoutRow, MAX_COLS);
        const dashboardRows = getDashboardRows(rowLayout, layoutRow, i);
        return (
          <Fragment key={`${layoutRow.title}-${i}`}>
            <LayoutTitle title={layoutRow.title} />
            {dashboardRows.map((row, i) => (
              <DashboardRow key={`dashboard-row-${i}`}>{row}</DashboardRow>
            ))}
          </Fragment>
        );
      })}
    </>
  );
}
